import type { RecordLocationRef, RecordLocationTarget, RecordTableRow } from './RecordsList';

/**
 * A single mortality registration as served by `Records/deaths`. Snake_case because it mirrors the
 * wire verbatim.
 *
 * One row is one Dead/Euthanized registration, anchored on the one location it happened at - unlike
 * Insertions and Transfers there is no other end to the move, so this carries no `from_*` chain.
 */
export type DeathApiRow = {
    record_id: number;
    /** Farm-local date of the registration. `yyyy-MM-dd`. */
    date: string;
    /**
     * Farm-local Monday of the week this registration bins into, `yyyy-MM-dd`. Server-computed, so
     * the page needs no week arithmetic of its own.
     */
    week_start: string;

    farm: RecordLocationRef;
    barn: RecordLocationRef;
    section: RecordLocationRef | null;
    /** The actual Pen/Batch the registration sits on - may resolve deeper than `section`. */
    location: RecordLocationTarget;

    amount: number;
    /* No underscore, unlike Insertion/Transfer's `avg_weight` - matches the legacy Deaths field. */
    avgweight: number | null;
    /** `avgweight * amount`, server-computed, so the table figure matches the formula modal's. */
    total_weight: number | null;
    deadoreuthanized: string;
    /** Null when no cause was registered. */
    cause: string | null;
    /** Resolved whenever `cause` carries a category; null alongside a null `cause`. */
    category: string | null;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/**
 * `DeathApiRow`, reshaped to the field names the shared records kit reads.
 *
 * The location is carried under `to_farm`/`to_barn`/`to_section`/`to_location` rather than fields of
 * its own: the shared `RecordsTable` only knows how to render and facet those ids, and a death has
 * exactly one location, so the `to_*` side stands for it with no `from_*` side alongside. `number`
 * and `avg_weight` replace `amount` and `avgweight` for the same reason - they are the exact field
 * names `RecordRow`'s aggregation (the weekly chart, the footer totals) reads off every records page.
 */
export type DeathRow = RecordTableRow & {
    to_location: RecordLocationTarget;
    deadoreuthanized: string;
    /** Null when no cause was registered. */
    cause: string | null;
    /** Resolved whenever `cause` carries a category; null alongside a null `cause`. */
    category: string | null;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/** `DeathApiRow` as the shared records kit reads it. */
export function mapDeathRow(raw: DeathApiRow): DeathRow {
    return {
        record_id: raw.record_id,
        date: raw.date,
        week_start: raw.week_start,
        to_farm: raw.farm,
        to_barn: raw.barn,
        to_section: raw.section,
        to_location: raw.location,
        number: raw.amount,
        avg_weight: raw.avgweight,
        total_weight: raw.total_weight,
        deadoreuthanized: raw.deadoreuthanized,
        cause: raw.cause,
        category: raw.category,
        created_by: raw.created_by,
    };
}
